import { Box, Flex, IconButton, Text, useToast } from '@chakra-ui/react';
import { Copy, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { JsonView, darkStyles, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

import styles from './JsonViewer.module.css';

type ExpandState = 'default' | 'expanded' | 'collapsed';

interface JsonViewerProps {
  /** JSON string, plain text string, or parsed JS object */
  data: unknown;
  /** 'light' (default) or 'dark' */
  colorMode?: 'light' | 'dark';
  /** Optional title shown above the viewer */
  title?: string | React.ReactNode;
  /** Show copy/expand/collapse toolbar (default: true) */
  showToolbar?: boolean;
  /** Depth of initially expanded nodes (default: 2) */
  initialExpandDepth?: number;
  /** Constrain viewer height with scroll */
  maxHeight?: string | number;
}

const dukeLightStyles = {
  ...defaultStyles,
  container: styles.container ?? '',
  label: styles.label ?? '',
  clickableLabel: styles.label ?? '',
  stringValue: styles.stringValue ?? '',
  numberValue: styles.numberValue ?? '',
  booleanValue: styles.booleanValue ?? '',
  nullValue: styles.nullValue ?? '',
  undefinedValue: styles.undefinedValue ?? '',
  punctuation: styles.punctuation ?? '',
  otherValue: styles.otherValue ?? '',
};

const dukeDarkStyles = {
  ...darkStyles,
  container: styles.containerDark ?? '',
  label: styles.labelDark ?? '',
  clickableLabel: styles.labelDark ?? '',
  stringValue: styles.stringValueDark ?? '',
  numberValue: styles.numberValueDark ?? '',
  booleanValue: styles.booleanValueDark ?? '',
  nullValue: styles.nullValueDark ?? '',
  undefinedValue: styles.undefinedValueDark ?? '',
  punctuation: styles.punctuationDark ?? '',
  otherValue: styles.otherValueDark ?? '',
};

function isObject(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}

function parseData(
  data: unknown,
): { parsed: object; isPlainText: false } | { parsed: string; isPlainText: true } {
  if (typeof data === 'string') {
    try {
      const result: unknown = JSON.parse(data);
      if (isObject(result)) {
        return { parsed: result, isPlainText: false };
      }
      return { parsed: data, isPlainText: true };
    } catch {
      return { parsed: data, isPlainText: true };
    }
  }
  if (isObject(data)) {
    return { parsed: data, isPlainText: false };
  }
  return { parsed: String(data), isPlainText: true };
}

export function JsonViewer({
  data,
  colorMode = 'light',
  title,
  showToolbar = true,
  initialExpandDepth = 2,
  maxHeight,
}: JsonViewerProps) {
  const [expandState, setExpandState] = useState<ExpandState>('default');
  const toast = useToast();

  const { parsed, isPlainText } = useMemo(() => parseData(data), [data]);

  const isDark = colorMode === 'dark';
  const bg = isDark ? 'gray.800' : 'bg.subtle';
  const borderColor = isDark ? 'gray.600' : 'border.default';
  const titleColor = isDark ? 'gray.200' : 'fg.emphasis';
  const textColor = isDark ? 'gray.100' : 'gray.800';
  const toolbarIconColor = isDark ? 'gray.300' : 'fg.subtle';

  const shouldExpandNode = useMemo(() => {
    switch (expandState) {
      case 'expanded':
        return () => true;
      case 'collapsed':
        return () => false;
      case 'default':
        return (level: number) => level < initialExpandDepth;
    }
  }, [expandState, initialExpandDepth]);

  const handleCopy = () => {
    const text = isPlainText ? String(parsed) : JSON.stringify(parsed, null, 2);
    void navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  const showTitleBar = title != null || showToolbar;

  return (
    <Box
      borderRadius="md"
      bg={bg}
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      w="full"
    >
      {showTitleBar ? (
        <Flex
          px={3}
          py={1}
          align="center"
          justify="space-between"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          {title != null ? (
            <Text fontSize="sm" fontWeight="medium" color={titleColor}>
              {title}
            </Text>
          ) : (
            <Box />
          )}
          {showToolbar ? (
            <Flex gap={1}>
              <IconButton
                aria-label="Copy JSON"
                icon={<Copy size={14} />}
                variant="ghost"
                size="xs"
                color={toolbarIconColor}
                onClick={handleCopy}
              />
              <IconButton
                aria-label="Expand all"
                icon={<ChevronsUpDown size={14} />}
                variant="ghost"
                size="xs"
                color={toolbarIconColor}
                onClick={() => setExpandState('expanded')}
              />
              <IconButton
                aria-label="Collapse all"
                icon={<ChevronsDownUp size={14} />}
                variant="ghost"
                size="xs"
                color={toolbarIconColor}
                onClick={() => setExpandState('collapsed')}
              />
            </Flex>
          ) : null}
        </Flex>
      ) : null}

      <Box
        p={3}
        overflowX="auto"
        overflowY={maxHeight != null ? 'auto' : undefined}
        maxHeight={maxHeight}
      >
        {isPlainText ? (
          <Box
            as="pre"
            margin={0}
            fontFamily="monospace"
            fontSize="0.85em"
            color={textColor}
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {String(parsed)}
          </Box>
        ) : (
          <JsonView
            data={parsed}
            style={isDark ? dukeDarkStyles : dukeLightStyles}
            shouldExpandNode={shouldExpandNode}
          />
        )}
      </Box>
    </Box>
  );
}
